import { Injectable } from "@angular/core";
import { DateGranularityEnum } from "src/app/shared/enum/date-granularity.enum";
import { ChartsDataByDay, FinOpsAwsOnOffCounters, FinOpsAwsOnOffResource, HistoricalDataByDate } from "./finops-aws-on-off.model";
import { Filter } from "src/app/store/preferences/filters/preferences-filters.model";

export type DataForDatatableCompute = { 
    measuresDatatable: { [id: string]: FinOpsAwsOnOffResource[]; },
    filteredCounters: { [id: string]: Record<string, FinOpsAwsOnOffCounters>; },
    previousTotalCounters: { [id: string]: FinOpsAwsOnOffCounters; }
}

@Injectable({
    providedIn: 'root'
})
export class FinOpsAwsOnOffResourcesHelper {

    static computeDataTable(historicalDatesSelected: string[], historicalDataByGranularity: HistoricalDataByDate, filters: Filter[]) {
        // compute data for datatable
        let measuresDatatable: { [id: string]: FinOpsAwsOnOffResource[]; } = {}
        let filteredCounters: { [id: string]: Record<string, FinOpsAwsOnOffCounters>; } = {}
        let previousTotalCounters: { [id: string]: FinOpsAwsOnOffCounters; } = {}

        for (const date of historicalDatesSelected) {
            let datatable: FinOpsAwsOnOffResource[] = []
            
            // Add each resources to datatable array
            Object.values(historicalDataByGranularity[date]).forEach(onoffResource => {
                datatable.push(onoffResource)
            })

            // Update datatable content according to current selected filters
            filters.forEach(filter => {
                if (filter.key == 'project') {
                    datatable = datatable.filter((resource) => {
                        let projectsFound = false
                        resource.projects.forEach(project => {
                            projectsFound = projectsFound || filter.value.includes(project)
                        })
                        return projectsFound
                    })
                } else if (filter.key == 'tag') {
                    datatable = datatable.filter((resource) => {
                        let tagFound = false
                        Object.entries(resource.tags).forEach((tag: any) => {
                            let tagKey = tag[0]
                            let tagValue = tag[1]
                            filter.value.forEach((filterValue: any) => {
                                let filterTagKey = filterValue.substring(0, filterValue.indexOf("="))
                                let filterTagValue = filterValue.substring(filterValue.indexOf("=") + 1)
                                tagFound = tagFound || (tagKey == filterTagKey && tagValue == filterTagValue)
                            });
                        })
                        return tagFound
                    })
                } else if (filter.key == 'age') {
                    let unknownAge = 99999
                    let filterAge: number = unknownAge
                    if (filter.value == 'today') {
                        filterAge = 0
                    } else if (filter.value.startsWith('last_')) {
                        filterAge = filter.value.substring(5, 7)
                    }
                    datatable = datatable.filter((resource) => {
                        let ageInRange = false
                        if (filterAge == unknownAge) {
                            ageInRange = resource.age == filterAge
                        } else {
                            ageInRange = resource.age <= filterAge
                        }
                        return ageInRange
                    })
                } else if (filter.key.startsWith('tag_')) {
                    let tagKey = filter.key.substring(4)
                    if (Array.isArray(filter.value)) {
                        datatable = datatable.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && filter.value.includes(pair[1])))
                    } else {
                        datatable = datatable.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && pair[1] === filter.value))
                    }
                } else {
                    if (Array.isArray(filter.value)) {
                        datatable = datatable.filter((resource) => filter.value.includes(resource[filter.key as keyof FinOpsAwsOnOffResource]))
                    } else {
                        datatable = datatable.filter((resource) => resource[filter.key as keyof FinOpsAwsOnOffResource] === filter.value)
                    }
                }
            });
            measuresDatatable[date] = datatable

            // Update counters for the measure
            let totalCounters: Record<string, FinOpsAwsOnOffCounters> = {
                "year": {
                    prediction: false,
                    previous_on_hours: 0,
                    on_hours: 0,
                    off_hours: 0,
                    day_ratio: 0,
                    gap_ratio: 0,
                    gap_value: 0
                },
                "month": {
                    prediction: false,
                    previous_on_hours: 0,
                    on_hours: 0,
                    off_hours: 0,
                    day_ratio: 0,
                    gap_ratio: 0,
                    gap_value: 0
                },
                "day": {
                    prediction: false,
                    previous_on_hours: 0,
                    on_hours: 0,
                    off_hours: 0,
                    day_ratio: 0,
                    gap_ratio: 0,
                    gap_value: 0
                }
            }

            datatable.forEach(row => {
                Object.entries(row.running_counters).forEach(entries => {
                    let granularity = entries[0] as DateGranularityEnum
                    let counters = entries[1]
                    totalCounters[granularity].prediction = counters.prediction
                    totalCounters[granularity].on_hours += Number(counters.on_hours)
                    totalCounters[granularity].off_hours += Number(counters.off_hours)
                    totalCounters[granularity].day_ratio = Number(counters.day_ratio)
                });
            });

            Object.keys(totalCounters).forEach(granularity => {
                totalCounters[granularity].on_hours = totalCounters[granularity].on_hours
                totalCounters[granularity].off_hours = totalCounters[granularity].off_hours
                totalCounters[granularity].day_ratio = totalCounters[granularity].day_ratio

                if (Object.keys(previousTotalCounters).length > 0) {
                    totalCounters[granularity].previous_on_hours = previousTotalCounters[granularity].on_hours
                    let gap_value = totalCounters[granularity].on_hours - totalCounters[granularity].previous_on_hours
                    totalCounters[granularity].gap_ratio = totalCounters[granularity].previous_on_hours == 0 ? 100 : ((gap_value / totalCounters[granularity].previous_on_hours) * 100)
                    totalCounters[granularity].gap_value = gap_value
                }
            });
            filteredCounters[date] = totalCounters
            previousTotalCounters = totalCounters
        }

        return {
            measuresDatatable: measuresDatatable,
            filteredCounters: filteredCounters,
            previousTotalCounters: previousTotalCounters
        }
    }

    static aggregateDataTables(
        historicalDatesSelected: string[],
        accountData: Record<string, DataForDatatableCompute>
    ): DataForDatatableCompute {
        // Init grand total
        const grandTotal: DataForDatatableCompute = {
            measuresDatatable: {},
            filteredCounters: {},
            previousTotalCounters: {}
        };

        // Init counters and arrays for each selected date
        for (const date of historicalDatesSelected) {
            grandTotal.measuresDatatable[date] = [];
            grandTotal.filteredCounters[date] = {
                year: this.emptyCounters(),
                month: this.emptyCounters(),
                day: this.emptyCounters()
            };
        }

        // Aggregate all accounts
        for (const data of Object.values(accountData)) {
            for (const date of historicalDatesSelected) {
                // Merge measures
                grandTotal.measuresDatatable[date] = [
                    ...grandTotal.measuresDatatable[date],
                    ...(data.measuresDatatable[date] || [])
                ];

                // Merge counters
                const dateCounters = data.filteredCounters[date];
                if (dateCounters) {
                    for (const granularity of Object.keys(dateCounters) as DateGranularityEnum[]) {
                        const src = dateCounters[granularity];
                        const dest = grandTotal.filteredCounters[date][granularity];

                        dest.on_hours += src.on_hours;
                        dest.off_hours += src.off_hours;
                        dest.previous_on_hours += src.previous_on_hours;
                        dest.gap_value += src.gap_value;
                        dest.gap_ratio = dest.previous_on_hours
                        ? (dest.gap_value / dest.previous_on_hours) * 100
                        : 100;
                        dest.prediction = dest.prediction || src.prediction;
                        dest.day_ratio = src.day_ratio; // last value kept
                    }
                }
            }
        }

        return grandTotal;
    }

    private static emptyCounters(): FinOpsAwsOnOffCounters {
        return {
            prediction: false,
            previous_on_hours: 0,
            on_hours: 0,
            off_hours: 0,
            day_ratio: 0,
            gap_ratio: 0,
            gap_value: 0
        };
    }

    static computeOnOffChartData(
        accountChartsData: ChartsDataByDay,
        targetDate: string,
        filters: Filter[],
    ) {

        // Init aggregator
        let filteredChartsData: FinOpsAwsOnOffResource[] = []

        accountChartsData[targetDate].forEach((measure: any) => {
            filteredChartsData.push(measure)
        });
        
        filters.forEach(filter => {
            if (filter.key == 'project') {
                filteredChartsData = filteredChartsData.filter((resource) => {
                    let projectsFound = false
                    resource.projects.forEach(project => {
                        projectsFound = projectsFound || filter.value.includes(project)
                    })
                    return projectsFound
                })
            } else if (filter.key == 'tag') {
                filteredChartsData = filteredChartsData.filter((resource) => {
                    let tagFound = false
                    Object.entries(resource.tags).forEach((tag: any) => {
                        let tagKey = tag[0]
                        let tagValue = tag[1]
                        filter.value.forEach((filterValue: any) => {
                            let filterTagKey = filterValue.substring(0, filterValue.indexOf("="))
                            let filterTagValue = filterValue.substring(filterValue.indexOf("=") + 1)
                            tagFound = tagFound || (tagKey == filterTagKey && tagValue == filterTagValue)
                        });
                    })
                    return tagFound
                })
            } else if (filter.key == 'age') {
                let unknownAge = 99999
                let filterAge: number = unknownAge
                if (filter.value == 'today') {
                    filterAge = 0
                } else if (filter.value.startsWith('last_')) {
                    filterAge = filter.value.substring(5, 7)
                }
                filteredChartsData = filteredChartsData.filter((resource) => {
                    let ageInRange = false
                    if (filterAge == unknownAge) {
                        ageInRange = resource.age == filterAge
                    } else {
                        ageInRange = resource.age <= filterAge
                    }
                    return ageInRange
                })
            } else if (filter.key.startsWith('tag_')) {
                let tagKey = filter.key.substring(4)
                if (Array.isArray(filter.value)) {
                    filteredChartsData = filteredChartsData.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && filter.value.includes(pair[1])))
                } else {
                    filteredChartsData = filteredChartsData.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && pair[1] === filter.value))
                }
            } else {
                if (Array.isArray(filter.value)) {
                    filteredChartsData = filteredChartsData.filter((resource) => filter.value.includes(resource[filter.key as keyof FinOpsAwsOnOffResource]))
                } else {
                    filteredChartsData = filteredChartsData.filter((resource) => resource[filter.key as keyof FinOpsAwsOnOffResource] === filter.value)
                }
            }
        });

        return filteredChartsData

    }

}